import { createAsyncThunk, AsyncThunk } from '@reduxjs/toolkit';
import { basicApiAsyncThunkCreator } from '../../services/apiAsyncThunkCreator';
import { ThunkAPI } from '../rootReducer';
import { SERVICES, HttpMethods } from '../../services/api';
import { fetchWhoAmI, userLogout } from './login.thunks';
import { IImpersonateUserRequest } from './login.thunks';
import { clearImpersonatedUser, setImpersonatedUser, setIsLoggedIn } from '.';
import { showErrorMessage } from '../notifications';

export interface IRefreshTokenResponse {
  token: string;
  userId: string;
}

export const refreshToken: AsyncThunk<IRefreshTokenResponse, void, ThunkAPI> = createAsyncThunk(
  'auth/refreshToken',
  async (_: void, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<IRefreshTokenResponse, void>({
        endpoint: 'refresh',
        microservice: SERVICES.AUTH_SERVICE,
        method: HttpMethods.Post,
        authenticated: false
      })(_, thunk);
      await thunk.dispatch(setIsLoggedIn());
      await thunk.dispatch({
        type: refreshToken.fulfilled.type,
        payload: response
      });
      thunk.dispatch(fetchWhoAmI());
      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  },
  {
    condition: (_: void, { getState }) => {
      const { auth: { loading } } = getState();
      if (loading) {
        return false;
      }
    }
  }
);

export const overrideSession: AsyncThunk<void, void, ThunkAPI> = createAsyncThunk(
  'auth/overrideSession',
  async (_: void, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, void>({
        endpoint: 'override',
        microservice: SERVICES.AUTH_SERVICE,
        method: HttpMethods.Post,
        authenticated: true
      })(undefined, thunk);
      return response;
    } catch (err) {
      thunk.dispatch(userLogout());
      return thunk.rejectWithValue(err);
    }
  }
);

/** @deprecated */
// Impersonate must not exist on non core
export const refreshImpersonatingSession: AsyncThunk<{token: string}, void, ThunkAPI> = createAsyncThunk(
  'auth/impersonate',
  async (_, thunk) => {
    try {
      const authState = thunk.getState().auth;
      const loggedInUser = (authState && authState.originalUser) || {};
      const impersonatedUser = authState && authState.user;
      const requestData = {
        impersonatedUserId: impersonatedUser ? impersonatedUser.id : '',
        authenticatedUser: loggedInUser
      };
      const response = await basicApiAsyncThunkCreator<{token: string}, IImpersonateUserRequest>({
        endpoint: 'impersonate',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(requestData, thunk);
      if(impersonatedUser) {
        setImpersonatedUser({ impersonatedUser, impersonatedToken: 'token' });
      }
      return response;
    } catch (err) {
      thunk.dispatch(clearImpersonatedUser());
      thunk.dispatch(showErrorMessage('User impersonation failed'));
      return thunk.rejectWithValue(err);
    }
  }
);
